// The SnappablePagerMixin extracts the functionality to make an element
// within a component snappable to achieve a pager effect as described
// in https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-snap-type

import { ScrollStateEnum } from '@/enum/scroll_state';
import FeatureGating from '@/services/FeatureGating';

// Percentage of the container that the user must scroll to traverse categories
const SCROLL_THRESHOLD_PERCENTAGE = 0.15;

// Coefficient used to compute the animation duration
const DURATION_COEFFICIENT = 3;

// Detects whether browser supports passive listener
// https://github.com/WICG/EventListenerOptions/blob/gh-pages/explainer.md#feature-detection
let supportsPassive = false;
try {
  const opts = Object.defineProperty({}, 'passive', {
    get: function() {
      supportsPassive = true;
      return supportsPassive;
    },
  });
  window.addEventListener('testPassive', null, opts);
  window.removeEventListener('testPassive', null, opts);
} catch (e) {} /* eslint-disable-line no-empty */

export default {
  data: function() {
    return {
      pagerElement: null,
      remainingDistance: 0,
    };
  },
  mounted: function() {
    const avatarBuilderCategoryContainerSelector = FeatureGating.isUpdatedTraitNavEnabled()
      ? '.avatar-builder-category-container-updated'
      : '.avatar-builder-category-container';
    const pagerElement = this.$el.querySelector(
      `${avatarBuilderCategoryContainerSelector} > [data-snappable-pager]`,
    );

    if (!pagerElement) {
      global.console.warn(
        'Cannot apply SnappablePager without a data-snappable-pager direct child element',
      );
      return;
    }

    this.pagerElement = pagerElement;

    if (this.pagerElement.dataset.snappablePager === 'horizontal') {
      this.pagerElement.style['overflow-x'] = 'visible';
      // 'touch' is required for ios scrolling momentum
      this.pagerElement.style['-webkit-overflow-scrolling'] = 'touch';
    } else if (this.pagerElement.dataset.snappablePager === 'vertical') {
      this.pagerElement.style['overflow-y'] = 'scroll';
      this.pagerElement.style['scroll-snap-type'] = 'y mandatory';
      this.pagerElement.style['-webkit-overflow-scrolling'] = 'touch';
    } else {
      throw new Error(`Invalid SnappablePager mode: ${this.pagerElement.dataset.snappablePager}`);
    }

    let touchStartPositionX;
    let touchStartPositionY;
    let previousTransXValue;
    let totalTransX;
    const touchEventReliable = this.isTouchEventReliable();

    const handleTouchStart = (event) => {
      touchStartPositionX = event.touches[0].clientX;
      touchStartPositionY = event.touches[0].clientY;
      const previousTransX = this.getTransform();
      if (previousTransX) {
        previousTransXValue = +previousTransX.split('(')[1].split('px')[0];
      }
    };

    const handleTouchMove = (event) => {
      const touch = event.touches[0] || event.changedTouches[0];
      let deltaX = touch.clientX - touchStartPositionX;
      let deltaY = touch.clientY - touchStartPositionY;
      totalTransX = deltaX;

      // Decide if the user is scrolling horizontally or vertically
      // Note that scroll state might not have correct value if touch event is not reliable, so
      // we always re-compute scroll state if touch event is not reliable
      if (!touchEventReliable || this.$store.getters.scrollState === ScrollStateEnum.IDLE) {
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          this.$store.commit('scrollState', ScrollStateEnum.HORIZONTAL);
        } else {
          this.$store.commit('scrollState', ScrollStateEnum.VERTICAL);
        }
      }

      if (this.$store.getters.scrollState !== ScrollStateEnum.HORIZONTAL) {
        // Do not scroll horizontally if the state is not horizontal.
        // This prevents (easily triggered) diagonal scrolls where horizontal
        // and vertical scrolls take place simultaneously
        return;
      }

      // The cause of touch events bug is that Android 4.4 considers all long scrolling movements
      // as being stuck and should be cancelled, even if when users are just scrolling slowing
      // into next category. In that situation, Android will fire touchCancel event instead
      // and discontinue firing any further touch events.
      //
      // As a workaround to this bug, calling preventDefault() method tells Android to "back off"
      // and leave scrolling to application code. That disables native scrolling. So we only want
      // to do it in horizontal scrolling, in which we use our own scrolling logic and don't rely
      // on native scrolling
      if (!touchEventReliable) {
        event.preventDefault();
      }

      // On desktop the touch event can be off the screen
      // No-op that case
      if (touch.clientX >= this.pagerElement.clientWidth || touch.clientX <= 0) {
        return;
      }

      // When user has already scrolled, we add the current delta to the previous position
      if (previousTransXValue) {
        totalTransX += previousTransXValue;
      }

      // Short circuit if scrolling out of bounds
      if (this.isScrollingOutOfBounds(totalTransX)) {
        return;
      }

      // force hardware acceleration
      const transformProperty = `translate3d(${totalTransX}px, 0, 0)`;
      this.setTransform(transformProperty);
    };

    const handleTouchEnd = (event) => {
      // We only transform the css if the user has been scrolling horizontally
      if (this.$store.getters.scrollState === ScrollStateEnum.HORIZONTAL) {
        const touchEndPositionX = event.changedTouches[0].clientX;
        const deltaX = touchEndPositionX - touchStartPositionX;

        // Short circuit touchEnd handler so we don't mistakenly set the CSS when the
        // categories don't change from the first category
        if (this.isScrollingOutOfBounds(totalTransX)) {
          this.$store.commit('scrollState', ScrollStateEnum.IDLE);
          this.pagerElement.style.transition = null;
          return;
        }

        if (deltaX >= scrollXTreshold) {
          this.goToPrevCategory();
        } else if (deltaX <= -1 * scrollXTreshold) {
          this.goToNextCategory();
        } else {
          this.scrollToCurrentCategory();
        }

        // This gives us how much more scrolling needs to be done to reach the previous/next category
        this.remainingDistance = this.pagerElement.clientWidth - Math.abs(deltaX);

        this.setTransformCSS();

        // Attempt to stop the tapping event from selecting an option by accident
        event.stopPropagation();
      }

      this.$store.commit('scrollState', ScrollStateEnum.IDLE);
    };

    const handleTransitionEnd = () => {
      // Remove the transition duration after complete so touch gesture isn't
      // animated and is more fluid
      this.pagerElement.style.transition = null;

      // Dispatch an action to the store that signals this animation has completed.
      this.$store.dispatch('categoryScrollTransitionEnd');
    };

    const scrollXTreshold = SCROLL_THRESHOLD_PERCENTAGE * this.pagerElement.clientWidth;

    // Note: passive listener won't call preventDefault()
    this.pagerElement.addEventListener(
      'touchstart',
      handleTouchStart,
      supportsPassive ? { passive: true } : false,
    );
    // handleTouchMove would call preventDefault() if touchEventReliable is false
    this.pagerElement.addEventListener(
      'touchmove',
      handleTouchMove,
      supportsPassive ? { passive: touchEventReliable } : false,
    );
    this.pagerElement.addEventListener(
      'touchend',
      handleTouchEnd,
      supportsPassive ? { passive: true } : false,
    );
    this.pagerElement.addEventListener(
      'transitionend',
      handleTransitionEnd,
      supportsPassive ? { passive: true } : false,
    );

    this.$store.commit('setScrollerFunction', this.SnappablePager_scrollToPage);
  },
  methods: {
    SnappablePager_goToPrevious: function() {
      if (!this.pagerElement) {
        return;
      }
      this.setTransformCSS();

      this.goToPrevCategory();
    },
    SnappablePager_goToNext: function() {
      if (!this.pagerElement) {
        return;
      }
      this.setTransformCSS();

      this.goToNextCategory();
    },
    SnappablePager_scrollToPage: function(pageIndex) {
      if (!this.pagerElement) {
        return;
      }
      this.setTransformCSS();

      // Note: not using clientWidth, since that only returns an integer value
      const translate = this.pagerElement.getBoundingClientRect().width * pageIndex;
      // force hardware acceleration
      const transformProperty = `translate3d(-${translate}px, 0, 0)`;
      this.setTransform(transformProperty);
    },
    pageSelected: function() {
      // no-op to be implemented by component
    },
    setTransformCSS: function() {
      const duration = this.getScrollAnimationDuration();
      this.pagerElement.style.transitionDuration = `${duration}s`;
    },
    scrollToCurrentCategory: function() {
      this.$store.commit('scrollToPage', { index: this.$store.getters.selectedCategoryIndex });
    },
    isScrollingOutOfBounds: function(scrollPosition) {
      // User scrolling to left of first category
      if (scrollPosition >= 0) {
        return true;
      }

      // User scrolling to right of last category
      if (
        Math.abs(scrollPosition) >
        window.innerWidth * (this.$store.getters.categories.length - 1)
      ) {
        return true;
      }

      return false;
    },
    goToNextCategory: function() {
      this.$store.dispatch('goToNextCategory');
    },
    goToPrevCategory: function() {
      this.$store.dispatch('goToPrevCategory');
    },
    getScrollAnimationDuration: function() {
      // Animation duration should be proportional to the remaining distance
      //  i.e. higher the distance, higher the duration.
      //  The equation below gives us a non-linear function of the remaining distance
      const duration = DURATION_COEFFICIENT * Math.pow(this.remainingDistance, 0.75);
      return Math.max(duration, 1) / 1e3;
    },
    getTransform() {
      return this.pagerElement.style.transform || this.pagerElement.style['-webkit-transform'];
    },
    setTransform(transformProperty) {
      this.pagerElement.style.transform = transformProperty;
      this.pagerElement.style['-webkit-transform'] = transformProperty;
    },
    isTouchEventReliable() {
      // Android 4.4 bug: touchMove and touchEnd events are not always fired
      // Reference: https://partnerissuetracker.corp.google.com/issues/36932783

      if (!navigator || !navigator.userAgent) {
        return true;
      }

      // Regex to match webview version number at group 1
      // Reference: https://developer.chrome.com/multidevice/user-agent
      const webViewRegex = new RegExp('Android.*Chrome/([0-9]+)[.0-9]* (Mobile)?', 'i');
      let webViewMatchResult = webViewRegex.exec(navigator.userAgent);

      if (!Array.isArray(webViewMatchResult) || webViewMatchResult.length < 2) {
        return true;
      }

      // Android L (5.0) is based on webview 36 above
      // Reference: https://developer.chrome.com/multidevice/webview/overview#what_version_of_chrome_is_it_based_on_
      const webViewVersion = webViewMatchResult[1];
      return webViewVersion >= 36;
    },
  },
};
